/**
 * Gets the sum of an array of numbers.
 *
 * @param {number[]} values an array to sum.
 * @returns {number} the sum of passed values.
 */
export const getSum = (values: number[]): number => {
  if (!values.length) return 0
  return values.reduce((acc: number, cur: number) => acc + cur, 0)
}

/** getSum type */
export type SumAggregator = typeof getSum

/**
 * Gets the average of an array of numbers.
 *
 * @param {number[]} values an array of numbers to average.
 * @returns {number} the average of passed values.
 */
export const getAvg = (values: number[]): number => {
  if (!values.length) return 0
  return values.reduce((acc: number, cur: number) => acc + cur, 0) / values.length
}

/** getAvg type */
export type AvgAggregator = typeof getAvg

/**
 * Calculates the weighted average, requiring values and weights arrays to have the same length.
 *
 * @param values The array of numbers to average.
 * @param weights The array of corresponding weights.
 * @returns The weighted average. Returns 0 if arrays are empty or total weight is 0.
 * @throws {Error} If the arrays have different lengths.
 */
export const getWeightedAvg = (values: number[], weights: number[]): number => {
  if (values.length !== weights.length) {
    throw new Error(
      `Values and weights arrays must have the same length. Received ${values.length} and ${weights.length}.`
    )
  }

  if (values.length === 0) {
    return 0
  }

  const weightedSum = values.reduce((acc, val, i) => acc + val * weights[i], 0)
  const totalWeight = weights.reduce((acc, weight) => acc + weight, 0)

  return totalWeight === 0 ? 0 : weightedSum / totalWeight
}

/** getWeightedAvg type */
export type WtAvgAggregator = typeof getWeightedAvg
